/**
 * Exact decimal money from the API.
 *
 * The API sends amounts as decimal STRINGS ("1234.50"). They are never turned
 * into a JS number for display or summing: a float cannot hold 0.1 + 0.2, and a
 * total that is off by a cent is a wrong total. Amounts are held here as a
 * BigInt count of millionths (6 decimal places, more than any currency needs),
 * so adding, subtracting and comparing are exact.
 */
import { getAppLocale } from './locale';
import { CURRENCY_NOT_SET, currencySymbol, isCurrencySet } from './currency';

// BigInt literals (`0n`) and `**` on bigint both need a newer `target` than
// this project builds with (es2015) — every constant and power below goes
// through BigInt(...) and a manual loop instead of the newer syntax.
const ZERO = BigInt(0);
const ONE_UNIT = BigInt(1);
const TEN = BigInt(10);
const TWO = BigInt(2);
const SCALE = 6;

/** 10^n as a bigint, without the `**` operator (unsupported on bigint below es2016). */
function pow10(n: number): bigint {
  let result = ONE_UNIT;
  for (let i = 0; i < n; i += 1) result *= TEN;
  return result;
}

const ONE = pow10(SCALE);
const PATTERN = /^([+-])?(\d*)(?:\.(\d*))?$/;

/** What an unknown or hidden amount (a contractor sees null) shows as. */
export const NO_AMOUNT = '—';

/** An amount in millionths, or null when `value` is not a plain decimal. */
export function toMicro(value: string | number | null | undefined): bigint | null {
  if (value === null || value === undefined) return null;
  const text = typeof value === 'number' ? (Number.isFinite(value) && !/e/i.test(String(value)) ? String(value) : '') : value.trim();
  if (text === '') return null;
  const m = PATTERN.exec(text);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) return null;
  const whole = BigInt(m[2] || '0');
  const frac = m[3] ?? '';
  let micro = whole * ONE + BigInt((frac.slice(0, SCALE)).padEnd(SCALE, '0'));
  // Round half up on the first dropped digit, so "0.0000005" is not silently lost.
  if (frac.length > SCALE && frac.charCodeAt(SCALE) >= 53) micro += ONE_UNIT;
  return m[1] === '-' ? -micro : micro;
}

/** Millionths back to a decimal string with `places` decimals (half away from zero). */
export function fromMicro(micro: bigint, places = 2): string {
  const negative = micro < ZERO;
  const abs = negative ? -micro : micro;
  const unit = pow10(SCALE - places);
  let scaled = abs / unit;
  if (abs % unit >= unit / TWO && unit > ONE_UNIT) scaled += ONE_UNIT;
  const digits = scaled.toString().padStart(places + 1, '0');
  const body = places > 0 ? `${digits.slice(0, -places)}.${digits.slice(-places)}` : digits;
  return negative && scaled !== ZERO ? `-${body}` : body;
}

/** Exact sum; a null or unreadable entry counts as nothing. */
export function sumDecimal(values: Array<string | number | null | undefined>): string {
  let total = ZERO;
  for (const v of values) total += toMicro(v) ?? ZERO;
  return fromMicro(total);
}

/** a - b, exact. Null when either side is unknown. */
export function subtractDecimal(a: string | null | undefined, b: string | null | undefined): string | null {
  const x = toMicro(a);
  const y = toMicro(b);
  return x === null || y === null ? null : fromMicro(x - y);
}

/** `value` times a whole number (a unit count), exact. Null when either is unusable. */
export function multiplyDecimalByInt(value: string | number | null | undefined, count: number): string | null {
  const x = toMicro(value);
  if (x === null || !Number.isInteger(count)) return null;
  return fromMicro(x * BigInt(count));
}

/** Exact product of two decimals as a 2-place string. Null when either is unusable. */
export function multiplyDecimals(a: string | number | null | undefined, b: string | number | null | undefined): string | null {
  const x = toMicro(a);
  const y = toMicro(b);
  return x === null || y === null ? null : fromMicro((x * y) / ONE);
}

/** |value| as a 2-place decimal string, or null when unreadable. */
export function absDecimal(value: string | number | null | undefined): string | null {
  const x = toMicro(value);
  return x === null ? null : fromMicro(x < ZERO ? -x : x);
}

/**
 * `part` as a percentage of `whole` (a ratio for display, not money). Null when
 * either side is unknown or the whole is zero.
 */
export function percentOf(part: string | number | null | undefined, whole: string | number | null | undefined): number | null {
  const p = toMicro(part);
  const w = toMicro(whole);
  if (p === null || w === null || w === ZERO) return null;
  return (Number(p) / Number(w)) * 100;
}

/** Sign of a - b (-1, 0, 1), or null when either side is unknown. */
export function compareDecimal(a: string | null | undefined, b: string | null | undefined): -1 | 0 | 1 | null {
  const x = toMicro(a);
  const y = toMicro(b);
  if (x === null || y === null) return null;
  return x < y ? -1 : x > y ? 1 : 0;
}

/** True when the amount is known and not zero. */
export function isNonZero(value: string | null | undefined): boolean {
  const x = toMicro(value);
  return x !== null && x !== ZERO;
}

function separators(): { group: string; decimal: string } {
  try {
    const parts = new Intl.NumberFormat(getAppLocale()).formatToParts(1111.1);
    return {
      group: parts.find(p => p.type === 'group')?.value ?? ',',
      decimal: parts.find(p => p.type === 'decimal')?.value ?? '.',
    };
  } catch {
    return { group: ',', decimal: '.' };
  }
}

/**
 * An API amount for display: symbol, grouped thousands, two decimals, built
 * from the string so no precision is lost.
 *  - null / unreadable -> an em dash (what a contractor sees),
 *  - no currency to label it with -> "currency not set" (never a guessed symbol).
 */
export function formatDecimal(value: string | number | null | undefined, currency?: string | null): string {
  const micro = toMicro(value);
  if (micro === null) return NO_AMOUNT;
  if (!isCurrencySet(currency)) return CURRENCY_NOT_SET;
  const { group, decimal } = separators();
  const fixed = fromMicro(micro, 2);
  const negative = fixed.startsWith('-');
  const [whole, cents] = (negative ? fixed.slice(1) : fixed).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  return `${negative ? '-' : ''}${currencySymbol(currency)}${grouped}${decimal}${cents}`;
}

/** Sums rows per currency (never across currencies): `{ GBP: "120.50", EUR: "30.00" }`. */
export function sumByCurrency<T>(
  rows: T[],
  pick: (row: T) => { amount: string | null | undefined; currency: string | null | undefined },
): Record<string, string> {
  const totals = new Map<string, bigint>();
  for (const row of rows) {
    const { amount, currency } = pick(row);
    const micro = toMicro(amount);
    if (micro === null || !isCurrencySet(currency)) continue;
    const code = currency.trim().toUpperCase();
    totals.set(code, (totals.get(code) ?? ZERO) + micro);
  }
  return Object.fromEntries([...totals.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([c, v]) => [c, fromMicro(v)]));
}

/** One formatted amount per currency ("£120.50", "€30.00"), for a card that stacks them instead of joining them. */
export function currencyTotalLines(totals: Record<string, string>): string[] {
  return Object.entries(totals).map(([code, amount]) => formatDecimal(amount, code));
}

/** "£120.50 · €30.00", or an em dash when there is nothing to show. */
export function formatCurrencyTotals(totals: Record<string, string>): string {
  const parts = Object.entries(totals).map(([code, amount]) => formatDecimal(amount, code));
  return parts.length > 0 ? parts.join(' · ') : NO_AMOUNT;
}
